import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { approvalService } from '@/services/approval.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import axios from 'axios';
import type { ItemPermissionsDto } from '@/types/release';

/**
 * Server verdicts of the release rulebook for the viewer on ONE post: the base post and each
 * variation (the server enforces, the UI mirrors). Used by the composers, which always know
 * the exact item they edit; lists keep the cheap client mirror in `usePostPermissions`.
 *
 * `base` / `forVariation(id)` are `undefined` while loading or when the post does not exist
 * yet (new post): callers fall back to the client mirror / "everything allowed" then.
 */
export interface PostReleasePermissions {
  isLoading: boolean;
  /** True while a (re)fetch is in flight, incl. background refetches: the cached verdict may be stale then. */
  isFetching: boolean;
  base: ItemPermissionsDto | undefined;
  forVariation: (variationId: number) => ItemPermissionsDto | undefined;
}

export const usePostReleasePermissions = (
  postId: number | null | undefined,
  enabled = true
): PostReleasePermissions => {
  const { teamId, hasTeam } = useEffectiveTeamId();
  // Only brands with an active release workflow have per-item verdicts worth asking for
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const workflowActive = isAgencyTeam && !carteBlanche;
  const id = postId ?? 0;

  const query = useQuery({
    queryKey: teamQueryKeys.approval.releasePermissions(teamId, id),
    networkMode: 'always',
    queryFn: () => approvalService.getPostReleasePermissions(id),
    enabled: enabled && workflowActive && hasTeam && id > 0,
    // Never retry a 4xx (no access / not found): the answer will not change
    retry: (failureCount, error) =>
      !(axios.isAxiosError(error) && (error.response?.status ?? 0) >= 400 && (error.response?.status ?? 0) < 500) &&
      failureCount < 1,
    // The verdict depends on state other people change (review decisions): never trust a cached one
    staleTime: 0,
    refetchOnMount: 'always',
  });

  return useMemo(() => {
    const items = query.data?.items ?? [];
    return {
      isLoading: enabled && workflowActive && id > 0 && query.isPending,
      isFetching: enabled && workflowActive && id > 0 && query.isFetching,
      base: items.find((i) => i.variationId === null)?.permissions,
      forVariation: (variationId: number) => items.find((i) => i.variationId === variationId)?.permissions,
    };
  }, [query.data, query.isPending, query.isFetching, enabled, workflowActive, id]);
};
